<template class="mr-3">
  <div class="card">
    <a-skeleton v-if="loading" :active="true" :paragraph="{ rows: 4 }" />

    <div v-else class="card-body">
      <a-tabs v-model="activeTab" @change="onTabChange">
        <a-button
          slot="tabBarExtraContent"
          type="primary"
          icon="rise"
          @click="goPromotion"
        >
          {{ $t("promotion.button") }}
        </a-button>
        <a-tab-pane key="normal">
          <span slot="tab">
            <a-icon type="book" />
            {{ $t("inscriptionStats.tabNormal") }}
          </span>
          <ClassroomTree
            :cache="normalCache"
            :level-labels="levelLabes"
            :show-deleted-classes="showDeletedClasses"
            :confirmed-classes-count="confirmedClassesCount"
            :total-classes-count="normalClasses.length"
            :all-classes-confirmed="allClassesConfirmed"
            :confirmation-loading="allClassesConfirmationLoading"
            @select="onSelect"
            @search="onSearch"
            @toggle-deleted="onToggleDeleted"
            @confirm-all="confirmAllClasses"
          />
        </a-tab-pane>

        <a-tab-pane key="affectation">
          <span slot="tab">
            <a-icon type="swap" />
            {{ $t("inscriptionStats.tabAffectation") }}
          </span>
          <AffectationList
            :cache="affectationCache"
            :level-labels="levelLabes"
            :show-deleted-classes="showDeletedClasses"
            :student-counts="studentCounts"
            @select="onSelect"
            @search="onSearch"
            @toggle-deleted="onToggleDeleted"
          />
        </a-tab-pane>

        <a-tab-pane key="indicators">
          <span slot="tab">
            <a-icon type="bar-chart" />
            {{ $t("inscriptionStats.tabIndicators") }}
          </span>
          <InscriptionIndicators v-if="indicatorsLoaded" :key="statsKey" />
        </a-tab-pane>
      </a-tabs>
    </div>
  </div>
</template>
<script>
/*eslint-disable */
import apiClient from "@/services/axios";
import router from "@/router";
import { mapState } from "vuex";
import { getLevels } from "@/config/LevelConstants";
import ClassroomTree from "./components/ClassroomTree.vue";
import AffectationList from "./components/AffectationList.vue";
import InscriptionIndicators from "./components/InscriptionIndicators.vue";

export default {
  components: { ClassroomTree, AffectationList, InscriptionIndicators },
  computed: {
    ...mapState(["settings", "user"]),
    activeBuilding() {
      return this.user.building.find(
        (el) => el.dbName == this.settings.activeBuilding
      );
    },
    countryCode() {
      return this.activeBuilding?.countryCode;
    },
    levelLabes() {
      return getLevels(this.$t.bind(this), this.schoolType, this.countryCode);
    },
    confirmedClassesCount() {
      return this.normalClasses.filter((c) => c.enrollmentConfirmed).length;
    },
    allClassesConfirmed() {
      return (
        this.normalClasses.length > 0 &&
        this.normalClasses.every((c) => c.enrollmentConfirmed)
      );
    },
    normalClasses() {
      return this.classes.filter((c) => c.classroomType !== "affectation");
    },
  },

  data() {
    return {
      schoolType: null,
      classes: [],
      showDeletedClasses: false,
      currentSearchTerm: "",
      classroomData: {},
      normalCache: {},
      affectationCache: {},
      studentCounts: {},
      loading: true,
      activeTab: "normal",
      indicatorsLoaded: false,
      statsKey: 0,
      allClassesConfirmationLoading: false,
    };
  },
  created() {
    this.schoolType = this.activeBuilding.type;

    Promise.all([
      apiClient.post("/classrooms/filter", {
        query: { includeAffectation: true },
        aggregation: {
          $project: {
            _id: 1,
            name: 1,
            level: 1,
            status: 1,
            classroomType: 1,
            enrollmentConfirmed: 1,
          },
        },
      }),
      apiClient.get("/classrooms/inscription-stats").catch(() => ({
        data: { classrooms: [] },
      })),
    ])
      .then(([classRes, statsRes]) => {
        const counts = {};
        ((statsRes.data && statsRes.data.classrooms) || []).forEach((c) => {
          counts[String(c._id)] = c.count || 0;
        });
        this.studentCounts = counts;

        // Keep object references so enrollmentConfirmed updates stay reactive in the tree
        this.classes = classRes.data.map((c) => ({
          ...c,
          enrollmentConfirmed: c.enrollmentConfirmed ?? true,
          studentCount: counts[String(c._id)] || 0,
        }));
        this.classroomData = _.groupBy(this.classes, "level");
        this.applyFilters();
      })
      .catch((e) => this.$message.error(this.$t("error.aucClasse")))
      .finally(() => (this.loading = false));
  },

  methods: {
    goPromotion() {
      router.push("/inscriptionScolarite/promotion");
    },
    onTabChange(key) {
      if (key === "indicators") {
        // Mount the dashboard lazily and refresh it every time it's opened.
        this.indicatorsLoaded = true;
        this.statsKey += 1;
      }
    },
    confirmAllClasses() {
      const content = this.allClassesConfirmed
        ? this.$t("inscription.cancelAllConfirmationContent")
        : this.$t("inscription.confirmAllClassesContent");
      this.$confirm({
        title: this.$t("all.sureTo"),
        content: (h) => <div style="color:red"> {content} </div>,
        onOk: () => this.updateAllClassesConfirmation(),
        onCancel: () => {},
      });
    },
    updateAllClassesConfirmation() {
      const newState = !this.allClassesConfirmed;
      this.allClassesConfirmationLoading = true;

      return apiClient
        .patch("/classrooms/enrollment-confirmation", {
          data: { enrollmentConfirmed: newState },
        })
        .then(() => {
          this.normalClasses.forEach((classroom) => {
            this.$set(classroom, "enrollmentConfirmed", newState);
          });
          this.$message.success(
            newState
              ? this.$t("inscription.allClassesConfirmedMsg")
              : this.$t("inscription.allClassesUnconfirmedMsg")
          );
        })
        .catch((error) => {
          console.error(error);
          this.$message.error(this.$t("error.impoModifi"));
          throw error;
        })
        .finally(() => {
          this.allClassesConfirmationLoading = false;
        });
    },
    onSelect(key) {
      if (!key) return;
      for (const item in this.classroomData) {
        this.classroomData[item].forEach((ac) => {
          if (key == ac._id) router.push(`/inscriptionScolarite/${key}`);
        });
      }
    },
    applyFilters() {
      const searchTerm = this.currentSearchTerm.toLowerCase();
      const normal = {};
      const affectation = {};

      for (let [key, value] of Object.entries(this.classroomData)) {
        let filtered = value;

        if (!this.showDeletedClasses) {
          filtered = filtered.filter((elem) => elem.status === "active");
        }
        if (searchTerm) {
          filtered = filtered.filter((elem) =>
            elem.name.toLowerCase().includes(searchTerm)
          );
        }

        normal[key] = filtered.filter(
          (elem) => elem.classroomType !== "affectation"
        );
        affectation[key] = filtered.filter(
          (elem) => elem.classroomType === "affectation"
        );
      }

      this.normalCache = normal;
      this.affectationCache = affectation;
    },
    onSearch(v) {
      this.currentSearchTerm = v || "";
      this.applyFilters();
    },
    onToggleDeleted(v) {
      this.showDeletedClasses = v;
      this.applyFilters();
    },
  },
};
</script>
<style scoped>
.card >>> .ant-tree {
  font-size: 16px;
}
.card >>> .ant-tree-title {
  font-weight: 500;
}
</style>
<style>
.enrollment-availability-btn,
.enrollment-availability-btn:hover,
.enrollment-availability-btn:focus,
.enrollment-availability-btn:active {
  background-color: #52c41a !important;
  border-color: #52c41a !important;
  color: #fff !important;
}
</style>
